/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: AI提取数据弹窗组件
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-10-29 16:05:08
 */
import React, { useState, useEffect } from 'react'
import { Modal, Button, Input, App } from 'antd'
import AiDashboard1 from '../../../images/photo-watermark/ai_dashboard.svg?react'
import AiDashboard2 from '../../../images/photo-watermark/ai_dashboard2.svg?react'
import AiDashboard3 from '../../../images/photo-watermark/ai_dashboard3.svg?react'
import AiDashboard4 from '../../../images/photo-watermark/ai_dashboard4.svg?react'
import AiDashboard5 from '../../../images/photo-watermark/ai_dashboard5.svg?react'
import AiDashboard6 from '../../../images/photo-watermark/ai_dashboard6.svg?react'
import AiDashboard7 from '../../../images/photo-watermark/ai_dashboard7.svg?react'
import CheckIcon from '../../../images/photo-watermark/icon_check_outlined.svg?react'
import './AiExtractModal.scss'
import { AI_EXTRACT_SCENARIOS } from '../lib/lib'
import sensors from '../../../utils/sensors'

interface IAiExtractModalProps {
  visible: boolean
  onCancel: () => void
  onSave: (selectedOptions: string[]) => void
  onCloseExtract?: () => void
  initialSelectedOptions?: string[] // 新增：初始选中的选项
  handleSensorClickModal: (clickItem: string, params: any) => void
}

interface ExtractOption {
  id: string
  title: string
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
}

const AiExtractModal: React.FC<IAiExtractModalProps> = ({
  visible,
  onCancel,
  onSave,
  onCloseExtract,
  initialSelectedOptions = [],
  handleSensorClickModal
}) => {
  const { message } = App.useApp()
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [customInputVisible, setCustomInputVisible] = useState(false)
  const [customInputValue, setCustomInputValue] = useState('')

  // 当弹窗打开时，同步初始选中值
  useEffect(() => {
    if (visible) {
      // 每次打开弹窗时，都从父组件获取当前已选中的场景
      setSelectedOptions(initialSelectedOptions)
      handleSensorClickModal('出现', {})
    }
  }, [visible, initialSelectedOptions])

  // 定义提取选项
  const extractOptions: ExtractOption[] = [
    {
      id: 'car_dashboard_mileage',
      title: '汽车仪表盘-里程',
      Icon: AiDashboard1
    },
    {
      id: 'fuel_gauge_liters',
      title: '加油表-升数',
      Icon: AiDashboard2
    },
    {
      id: 'fuel_gauge_unit_price',
      title: '加油表-单价',
      Icon: AiDashboard3
    },
    {
      id: 'fuel_gauge_total_price',
      title: '加油表-总价',
      Icon: AiDashboard3
    },
    {
      id: 'water_meter_cubic',
      title: '水表-立方数',
      Icon: AiDashboard4
    },
    {
      id: 'electricity_meter_degrees',
      title: '电表-度数',
      Icon: AiDashboard5
    },
    // {
    //   id: 'text_content_extract',
    //   title: '文本内容提取',
    //   Icon: AiDashboard6
    // },
    {
      id: 'other_data_extract',
      title: '我需要提取其他数据',
      Icon: AiDashboard7
    }
  ]

  // 处理选项选择
  const handleOptionToggle = (optionId: string) => {
    // 如果是"我需要提取其他数据"，显示自定义输入弹窗
    if (optionId === 'other_data_extract') {
      handleSensorClickModal('选型', {
        option: '其他'
      })
      handleCustomSensor('进入')
      setCustomInputVisible(true)
      return
    }
    handleSensorClickModal('选型', {
      option: AI_EXTRACT_SCENARIOS[optionId]?.title
    })

    setSelectedOptions((prev) => {
      if (prev.includes(optionId)) {
        return prev.filter((id) => id !== optionId)
      } else {
        return [...prev, optionId]
      }
    })
  }

  // 处理保存
  const handleSave = () => {
    const validOptions = selectedOptions.filter(
      (option) => option !== 'other_data_extract'
    )
    if (validOptions.length === 0) {
      message.warning('请至少选择一个提取选项')
      return
    }
    handleSensorClickModal('保存', {
      list1: validOptions.map((option) => AI_EXTRACT_SCENARIOS[option].title)
    })

    onSave(selectedOptions)
  }

  // 处理取消
  const handleCancel = (clickItem: string) => {
    const validOptions = selectedOptions.filter(
      (option) => option !== 'other_data_extract'
    )
    handleSensorClickModal(clickItem, {
      list1: validOptions.map((option) => AI_EXTRACT_SCENARIOS[option].title)
    })
    onCancel()
    // 清空选择状态，恢复到初始状态
    setSelectedOptions(initialSelectedOptions)
  }

  // 处理关闭提取功能
  const handleCloseExtract = () => {
    handleSensorClickModal('关闭功能', {})
    if (onCloseExtract) {
      onCloseExtract()
    }
    onCancel()
    // 清空选择状态
    setSelectedOptions([])
  }

  const handleCustomSensor = (actionType: string, params: any = {}) => {
    sensors.track('other_b4366_c7687_record', {
      actionType,
      ...params,
      track_sign: 'aother.b4366.c7687.record.other_b4366_c7687_record',
      requirement_id: 387
    })
  }

  // 处理自定义输入弹窗确认
  const handleCustomInputConfirm = () => {
    if (!customInputValue.trim()) {
      message.warning('请输入您关注的数据')
      return
    }

    handleCustomSensor('确定', {
      content: customInputValue
    })
    // 显示toast提示
    message.success('提取文本内容,敬请期待')

    // 关闭自定义输入弹窗
    setCustomInputVisible(false)
    setCustomInputValue('')
  }

  // 处理自定义输入弹窗取消
  const handleCustomInputCancel = () => {
    handleCustomSensor('取消', {})
    setCustomInputVisible(false)
    setCustomInputValue('')
  }

  return (
    <>
      <Modal
        title="AI提取数据"
        open={visible}
        onCancel={() => handleCancel('关闭')}
        footer={null}
        width={600}
        className="ai-extract-modal"
      >
        <div className="ai-extract-content">
          <p className="instruction-text">请选择您想要提取的数据内容(可多选)</p>

          <div className="options-grid">
            {extractOptions.map((option) => (
              <div
                key={option.id}
                className={`option-card ${
                  selectedOptions.includes(option.id) ? 'selected' : ''
                }`}
                onClick={() => handleOptionToggle(option.id)}
              >
                <div className="checkbox">
                  {selectedOptions.includes(option.id) && (
                    <div className="checkmark">
                      <CheckIcon />
                    </div>
                  )}
                </div>
                <div className="option-icon">
                  <option.Icon />
                </div>
                <div className="option-title">{option.title}</div>
              </div>
            ))}
          </div>

          <div className="modal-footer">
            <Button
              type="link"
              className="close-extract-btn"
              onClick={handleCloseExtract}
            >
              关闭提取功能
            </Button>
            <div className="button-container">
              <Button
                className="cancel-btn"
                onClick={() => handleCancel('取消')}
              >
                取消
              </Button>
              <Button type="primary" className="save-btn" onClick={handleSave}>
                保存
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* 自定义输入弹窗 */}
      <Modal
        title="你需要提取什么数据呢"
        open={customInputVisible}
        onCancel={handleCustomInputCancel}
        footer={null}
        width={400}
        className="custom-input-modal"
      >
        <div className="custom-input-content">
          <Input.TextArea
            value={customInputValue}
            onChange={(e) => setCustomInputValue(e.target.value)}
            placeholder="请输入你关注的数据,我们会尽快实现"
            rows={4}
            maxLength={200}
            className="custom-textarea"
          />

          <div className="custom-input-footer">
            <Button className="cancel-btn" onClick={handleCustomInputCancel}>
              取消
            </Button>
            <Button
              type="primary"
              className="confirm-btn"
              onClick={handleCustomInputConfirm}
            >
              确定
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AiExtractModal
